const FRAME_END = 63;
const ZERO_KEY = "#00000000";
const KEY_SIZE = 4;

function clone(value) {
  return value && typeof value === "object"
    ? JSON.parse(JSON.stringify(value))
    : {};
}

function list(value) {
  return Array.isArray(value) ? value : [];
}

function uint16BE(bytes, offset) {
  return (bytes[offset] << 8) | bytes[offset + 1];
}

function uint16LE(bytes, offset) {
  return bytes[offset] | (bytes[offset + 1] << 8);
}

function key(bytes, offset) {
  if (offset + KEY_SIZE > FRAME_END) return ZERO_KEY;
  return (
    "#" +
    Array.from(bytes.slice(offset, offset + KEY_SIZE), (item) =>
      item.toString(16).padStart(2, "0")
    )
      .join("")
      .toUpperCase()
  );
}

function isMacroTrigger(value) {
  // Firmware variants also return regular key values (for example
  // #00070013) as macro triggers.  The 06 0A packet is authoritative; only
  // discard an all-zero placeholder.
  return (
    typeof value === "string" &&
    /^#[0-9A-F]{8}$/i.test(value) &&
    value.toUpperCase() !== ZERO_KEY
  );
}

function color(bytes, offset) {
  return (
    "#" +
    Array.from(bytes.slice(offset, offset + 3), (item) =>
      item.toString(16).padStart(2, "0")
    )
      .join("")
      .toUpperCase()
  );
}

function unicode(bytes, offset) {
  if (offset + 1 >= FRAME_END) return null;
  return (
    "#" +
    Array.from(bytes.slice(offset, offset + 2), (item) =>
      item.toString(16).padStart(2, "0")
    )
      .join("")
      .toUpperCase()
  );
}

function isCommand(frame, command, subcommand) {
  return frame[0] === command && frame[1] === subcommand;
}

function framesOf(uploadFrames, command, subcommand) {
  return uploadFrames.filter((frame) => isCommand(frame, command, subcommand));
}

function commandFrames(uploadFrames, command) {
  return uploadFrames.filter((frame) => frame[0] === command);
}

function isFullUploadScope(scope) {
  return !scope || (scope.command === 0xff && scope.subcommand === 0xff);
}

function scopeMatches(scope, command, subcommand) {
  if (isFullUploadScope(scope)) return true;
  return (
    scope.command === command &&
    (scope.subcommand === 0xff || scope.subcommand === subcommand)
  );
}

function keyLayerScope(scope) {
  return scopeMatches(scope, 0xa6, 0x07) || scopeMatches(scope, 0xa6, 0x08);
}

function assertUniquePackets(source, getPacketKey, label) {
  const seen = new Set();
  source.forEach((frame) => {
    const packetKey = getPacketKey(frame);
    if (seen.has(packetKey)) {
      throw new Error(`R6 CDC ${label} 收到重复分包 ${packetKey}`);
    }
    seen.add(packetKey);
  });
}

function assertCompletePackets(
  source,
  getGroupKey,
  getPacketIndex,
  packetCount,
  label
) {
  const groups = new Map();
  source.forEach((frame) => {
    const groupKey = getGroupKey(frame);
    const packets = groups.get(groupKey) || new Set();
    packets.add(getPacketIndex(frame));
    groups.set(groupKey, packets);
  });
  groups.forEach((packets, groupKey) => {
    const complete =
      packets.size === packetCount &&
      Array.from({ length: packetCount }, (_, index) => packets.has(index)).every(
        Boolean
      );
    if (!complete) {
      throw new Error(
        `R6 CDC ${label} 分包不完整，分组 ${groupKey} 期望 ${packetCount} 包`
      );
    }
  });
}

function assertKeyLayerPackets(source, layerNum) {
  const expected = Math.ceil((layerNum * 25 * KEY_SIZE) / 60);
  const indexes = source
    .map((frame) => frame[2])
    .sort((left, right) => left - right);
  if (
    indexes.length !== expected ||
    new Set(indexes).size !== expected ||
    indexes.some((index, position) => index !== position)
  ) {
    throw new Error(`R6 CDC 键层分包不完整，期望 ${expected} 包`);
  }
}

function validateIndexedPackets(uploadFrames) {
  assertUniquePackets(
    framesOf(uploadFrames, 0xa2, 0x02),
    (frame) => frame[3],
    "页面控制"
  );
  assertUniquePackets(
    framesOf(uploadFrames, 0xa3, 0x01),
    (frame) => `${frame[3]}:${frame[2]}`,
    "文字"
  );
  assertUniquePackets(
    commandFrames(uploadFrames, 0xa4),
    (frame) => `${frame[1]}:${frame[2]}:${frame[3]}:${frame[4]}`,
    "页面 RGB"
  );
  assertCompletePackets(
    commandFrames(uploadFrames, 0xa4),
    (frame) => `${frame[1]}:${frame[2]}`,
    (frame) => frame[4],
    11,
    "页面 RGB"
  );
  assertUniquePackets(
    commandFrames(uploadFrames, 0xa5),
    (frame) => `${frame[1]}:${frame[2]}:${frame[3]}`,
    "键下灯 RGB"
  );
  assertCompletePackets(
    commandFrames(uploadFrames, 0xa5),
    (frame) => `${frame[1]}:${frame[2]}`,
    (frame) => frame[3],
    5,
    "键下灯 RGB"
  );
  assertUniquePackets(
    framesOf(uploadFrames, 0xa6, 0x05),
    (frame) => `${frame[4]}:${frame[9]}`,
    "宏"
  );
  assertUniquePackets(
    framesOf(uploadFrames, 0xa6, 0x07),
    (frame) => frame[2],
    "键层"
  );
  assertUniquePackets(
    uploadFrames.filter((frame) => frame[0] === 0x0b),
    (frame) => `${frame[1]}:${frame[2]}:${frame[3]}`,
    "车头灯"
  );
  assertCompletePackets(
    uploadFrames.filter((frame) => frame[0] === 0x0b),
    (frame) => `${frame[1]}:${frame[2]}`,
    (frame) => frame[3],
    2,
    "车头灯"
  );
}

function pageTemplate(index, base) {
  return {
    valid: 0,
    page_index: index,
    lightness: 0,
    speed_ms: 0,
    color: { default: false, back_rgb: "#000000", rgb: "#000000" },
    word_page: { valid: 0, word_len: 0, unicode: [] },
    frames: { valid: 0, frame_num: 0, frame_data: [] },
    keyframes: { valid: 0, frame_num: 0, frame_data: [] },
    ...clone(base),
  };
}

function preparePages(data, count) {
  const basePages = list(data.page_data);
  data.page_num = count;
  data.page_data = Array.from({ length: count }, (_, index) =>
    pageTemplate(index, basePages[index])
  );
}

function applyUncertainty(data, uploadFrames) {
  const frame = framesOf(uploadFrames, 0xa2, 0x01)[0];
  if (!frame) return;
  const count = frame[2];
  if (count !== data.page_num) preparePages(data, count);
  for (let index = 0, offset = 3; index < count; index += 1, offset += 6) {
    const page = data.page_data[index];
    if (!page || offset + 5 >= FRAME_END) break;
    page.page_index = frame[offset];
    page.word_page.word_len = frame[offset + 1];
    page.frames.frame_num = uint16LE(frame, offset + 2);
    page.keyframes.frame_num = uint16LE(frame, offset + 4);
  }
}

function applyPageControls(data, uploadFrames) {
  framesOf(uploadFrames, 0xa2, 0x02).forEach((frame) => {
    const count = frame[4];
    for (let index = 0; index < count; index += 1) {
      const offset = 5 + index * 12;
      if (offset + 11 >= FRAME_END) break;
      const page = data.page_data.find(
        (item) => item.page_index === frame[offset + 1]
      );
      if (!page) continue;
      page.valid = frame[offset];
      page.lightness = frame[offset + 2];
      page.speed_ms = uint16LE(frame, offset + 3);
      page.color = {
        ...page.color,
        default: Boolean(frame[offset + 5]),
        back_rgb: color(frame, offset + 6),
        rgb: color(frame, offset + 9),
      };
    }
  });
}

function applyValidBits(data, uploadFrames) {
  const frame = framesOf(uploadFrames, 0xa2, 0x05)[0];
  if (!frame) return;
  const count = Math.min(frame[2], data.page_data.length);
  for (let index = 0; index < count; index += 1) {
    const offset = 3 + index * 4;
    const page = data.page_data[index];
    page.valid = frame[offset];
    page.word_page.valid = frame[offset + 1];
    page.frames.valid = frame[offset + 2];
    page.keyframes.valid = frame[offset + 3];
  }
  if (frame[3 + frame[2] * 4] !== undefined) {
    data.key_layer.valid = frame[3 + frame[2] * 4];
  }
}

function parseWordPages(data, uploadFrames) {
  const source = framesOf(uploadFrames, 0xa3, 0x01);
  if (!source.length) return;
  const pages = {};
  source.forEach((frame) => {
    const pageIndex = frame[3];
    const page = pages[pageIndex] || {
      pageIndex,
      valid: frame[4],
      packets: {},
    };
    const values = [];
    const count = Math.min(frame[5], Math.floor((FRAME_END - 6) / 2));
    for (let index = 0; index < count; index += 1) {
      const value = unicode(frame, 6 + index * 2);
      if (value === null) break;
      values.push(value);
    }
    page.packets[frame[2]] = values;
    pages[pageIndex] = page;
  });
  Object.values(pages).forEach((item) => {
    const page = data.page_data.find(
      (entry) => entry.page_index === item.pageIndex
    );
    if (!page) return;
    const values = [];
    Object.keys(item.packets)
      .sort((left, right) => Number(left) - Number(right))
      .forEach((packet) => values.push(...item.packets[packet]));
    page.word_page = {
      ...page.word_page,
      valid: item.valid,
      word_len: values.length,
      unicode: values,
    };
  });
}

function parseKeyLayer(data, uploadFrames, strict) {
  const control = framesOf(uploadFrames, 0xa6, 0x08)[0];
  const layerFrames = framesOf(uploadFrames, 0xa6, 0x07);
  if (!control) return;
  const layerNum = control[2];
  if (strict && layerNum > 0) assertKeyLayerPackets(layerFrames, layerNum);
  if (!layerFrames.length) return;
  const bytes = [];
  layerFrames
    .slice()
    .sort((left, right) => left[2] - right[2])
    .forEach((frame) => bytes.push(...frame.slice(3, FRAME_END)));
  const layerData = Array.from({ length: layerNum }, (_, layerIndex) => ({
    layer: Array.from({ length: 25 }, (_, keyIndex) =>
      key(bytes, (layerIndex * 25 + keyIndex) * KEY_SIZE)
    ),
  }));
  data.key_layer = {
    ...data.key_layer,
    layer_num: layerNum,
    layer_data: layerData,
  };
}

function trimKeys(values) {
  const result = values.slice();
  while (result.length && result[result.length - 1] === ZERO_KEY) result.pop();
  return result;
}

function parseExchangeKeys(data, uploadFrames) {
  const source = framesOf(uploadFrames, 0xa6, 0x01);
  if (!source.length) return;
  data.exchange_num = source[0][2];
  data.exchange_key = source.map((frame) => ({
    exchange_index: frame[3],
    input_key: trimKeys(
      Array.from({ length: 5 }, (_, index) => key(frame, 4 + index * KEY_SIZE))
    ),
    out_key: trimKeys(
      Array.from({ length: 5 }, (_, index) => key(frame, 24 + index * KEY_SIZE))
    ),
  }));
}

function parseFnKeys(data, uploadFrames) {
  const source = framesOf(uploadFrames, 0xa6, 0x04);
  if (!source.length) return;
  data.Fn_key_num = source[0][2];
  data.Fn_key = [];
  source.forEach((frame) => {
    const count = frame[3];
    for (let index = 0; index < count; index += 1) {
      const offset = 4 + index * 9;
      if (offset + 8 >= FRAME_END) break;
      data.Fn_key.push({
        Fn_key_index: frame[offset],
        input_key: key(frame, offset + 1),
        out_key: key(frame, offset + 5),
      });
    }
  });
}

function parseSwapKeys(data, uploadFrames) {
  const source = framesOf(uploadFrames, 0xa6, 0x06);
  if (!source.length) return;
  data.swap_key_num = source.reduce((total, frame) => total + frame[3], 0);
  data.swap_key = [];
  source.forEach((frame) => {
    for (let index = 0; index < frame[3]; index += 1) {
      const offset = 4 + index * 9;
      if (offset + 8 >= FRAME_END) break;
      data.swap_key.push({
        swap_key_index: frame[offset],
        input_key: key(frame, offset + 1),
        out_key: key(frame, offset + 5),
      });
    }
  });
}

function parseMacros(data, uploadFrames) {
  const source = framesOf(uploadFrames, 0xa6, 0x05);
  if (!source.length) return;
  const macros = {};
  source.forEach((frame) => {
    const index = frame[4];
    const inputKey = key(frame, 5);
    if (!isMacroTrigger(inputKey)) return;
    const item = macros[index] || {
      MACRO_key_index: index,
      input_key: inputKey,
      packets: {},
    };
    const packet = { out_key: [], intvel_ms: [] };
    for (let slot = 0; slot < 8; slot += 1) {
      const output = key(frame, 10 + slot * KEY_SIZE);
      if (output === ZERO_KEY) continue;
      packet.out_key.push(output);
      packet.intvel_ms.push(uint16BE(frame, 42 + slot * 2));
    }
    item.packets[frame[9]] = packet;
    macros[index] = item;
  });
  const protocolMacros = Object.keys(macros)
    .sort((left, right) => Number(left) - Number(right))
    .map((index) => {
      const item = macros[index];
      const outKey = [];
      const intervals = [];
      Object.keys(item.packets)
        .sort((left, right) => Number(left) - Number(right))
        .forEach((packetIndex) => {
          outKey.push(...item.packets[packetIndex].out_key);
          intervals.push(...item.packets[packetIndex].intvel_ms);
        });
      return {
        MACRO_key_index: item.MACRO_key_index,
        input_key: item.input_key,
        out_key: outKey,
        intvel_ms: intervals,
      };
    });
  data.MACRO_key_num = protocolMacros.length;
  data.MACRO_key = protocolMacros;
  data.macro_key = protocolMacros.map((item) => ({
    MACRO_key_index: item.MACRO_key_index,
    original_key: item.input_key,
    layer_key: item.out_key.slice(),
    intvel_ms: item.intvel_ms.slice(),
  }));
}

function parseLegacyMacros(data, uploadFrames) {
  const source = framesOf(uploadFrames, 0x06, 0x0a);
  if (!source.length) return;
  const macros = {};
  source.forEach((frame) => {
    const index = frame[4];
    const inputKey = key(frame, 5);
    if (!isMacroTrigger(inputKey)) return;
    const item = macros[index] || {
      MACRO_key_index: index,
      input_key: inputKey,
      packets: {},
    };
    const packet = { out_key: [], intvel_ms: [] };
    for (let offset = 10; offset + 5 < FRAME_END; offset += 6) {
      const output = key(frame, offset);
      if (output === ZERO_KEY) continue;
      packet.out_key.push(output);
      packet.intvel_ms.push(uint16BE(frame, offset + 4));
    }
    item.packets[frame[9]] = packet;
    macros[index] = item;
  });
  const protocolMacros = Object.keys(macros)
    .sort((left, right) => Number(left) - Number(right))
    .map((index) => {
      const item = macros[index];
      const outKey = [];
      const intervals = [];
      Object.keys(item.packets)
        .sort((left, right) => Number(left) - Number(right))
        .forEach((packetIndex) => {
          outKey.push(...item.packets[packetIndex].out_key);
          intervals.push(...item.packets[packetIndex].intvel_ms);
        });
      return {
        MACRO_key_index: item.MACRO_key_index,
        input_key: item.input_key,
        out_key: outKey,
        intvel_ms: intervals,
      };
    });
  data.MACRO_key_num = protocolMacros.length;
  data.MACRO_key = protocolMacros;
  data.macro_key = protocolMacros.map((item) => ({
    MACRO_key_index: item.MACRO_key_index,
    original_key: item.input_key,
    layer_key: item.out_key.slice(),
    intvel_ms: item.intvel_ms.slice(),
  }));
}

function findKeyLayerLocation(data, targetKey) {
  const layers = list(data.key_layer?.layer_data);
  for (let layerIndex = 0; layerIndex < layers.length; layerIndex += 1) {
    const keys = list(layers[layerIndex]?.layer);
    const keyIndex = keys.indexOf(targetKey);
    if (keyIndex >= 0) {
      return {
        layer_num: layerIndex,
        row_num: Math.floor(keyIndex / 25),
        index: keyIndex % 25,
      };
    }
  }
  return {};
}

function parsePressHold(data, uploadFrames) {
  const responses = uploadFrames.filter(
    (frame) => isCommand(frame, 0x06, 0x0e) || isCommand(frame, 0xa6, 0x0e)
  );
  if (!responses.length) return;
  const source = responses.filter(
    (frame) =>
      key(frame, 6) !== ZERO_KEY &&
      !(
        key(frame, 12) === ZERO_KEY &&
        key(frame, 16) === ZERO_KEY &&
        uint16BE(frame, 10) === 0
      )
  );
  data.tab_key = source.map((frame) => {
    const phKey = key(frame, 6);
    return {
      ph_key_data: phKey,
      first_key: key(frame, 12),
      second_key: key(frame, 16),
      interval_time: uint16BE(frame, 10),
      pack_total_num: uint16BE(frame, 2),
      pack_num: frame[5],
      ...findKeyLayerLocation(data, phKey),
    };
  });
  data.tab_key_num = data.tab_key.length;
}

function collectRgb(source, offset, totalBytes, packetLength, getFrameInfo) {
  const groups = {};
  source.forEach((frame) => {
    const { pageIndex, frameIndex, packet } = getFrameInfo(frame);
    const group = groups[`${pageIndex}:${frameIndex}`] || {
      pageIndex,
      frameIndex,
      packets: {},
    };
    group.packets[packet] = Array.from(
      frame.slice(offset, offset + packetLength(packet))
    );
    groups[`${pageIndex}:${frameIndex}`] = group;
  });
  return Object.values(groups).map((group) => {
    const bytes = [];
    Object.keys(group.packets)
      .sort((left, right) => Number(left) - Number(right))
      .forEach((packet) => bytes.push(...group.packets[packet]));
    const rgb = bytes.slice(0, totalBytes);
    const frameRGB = [];
    for (let index = 0; index + 2 < rgb.length; index += 3) {
      frameRGB.push(color(rgb, index));
    }
    return {
      pageIndex: group.pageIndex,
      frameIndex: group.frameIndex,
      frameRGB,
    };
  });
}

function pageRgbFrameInfo(frame) {
  return {
    pageIndex: frame[1],
    frameIndex: frame[2] | (frame[3] << 8),
    packet: frame[4],
  };
}

function compactRgbFrameInfo(frame) {
  return {
    pageIndex: frame[1],
    frameIndex: frame[2],
    packet: frame[3],
  };
}

function applyRgbFrames(data, uploadFrames) {
  const pageFrames = collectRgb(
    commandFrames(uploadFrames, 0xa4),
    5,
    600,
    (packet) => (packet < 10 ? 56 : 40),
    pageRgbFrameInfo
  );
  const keyFrames = collectRgb(
    commandFrames(uploadFrames, 0xa5),
    4,
    270,
    (packet) => (packet < 4 ? 56 : 46),
    compactRgbFrameInfo
  );
  applyCollectedRgb(data, pageFrames, "frames");
  applyCollectedRgb(data, keyFrames, "keyframes");
}

function applyCollectedRgb(data, rgbFrames, targetName) {
  const byPage = {};
  rgbFrames.forEach((item) => {
    if (!byPage[item.pageIndex]) byPage[item.pageIndex] = [];
    byPage[item.pageIndex].push({
      frame_index: item.frameIndex,
      frame_RGB: item.frameRGB,
    });
  });
  Object.keys(byPage).forEach((pageIndex) => {
    const page = data.page_data.find(
      (entry) => entry.page_index === Number(pageIndex)
    );
    if (!page) return;
    const frameData = byPage[pageIndex].sort(
      (left, right) => left.frame_index - right.frame_index
    );
    page[targetName] = {
      ...page[targetName],
      frame_data: frameData,
      frame_num: Math.max(page[targetName]?.frame_num || 0, frameData.length),
    };
  });
}

function applyCarLightFrames(data, uploadFrames) {
  const source = uploadFrames.filter((frame) => frame[0] === 0x0b);
  const frames = collectRgb(
    source,
    4,
    72,
    (packet) => (packet === 0 ? 56 : 16),
    compactRgbFrameInfo
  );
  applyCollectedRgb(data, frames, "spotlight_frames");
}

function clearRgbData(page, targetName) {
  if (!page[targetName]) return;
  page[targetName] = {
    ...page[targetName],
    valid: 0,
    frame_num: 0,
    frame_data: [],
  };
}

function clearUploadedCollections(data, scope) {
  if (!isFullUploadScope(scope)) {
    if (scopeMatches(scope, 0xa3, 0x01)) {
      data.page_data.forEach((page) => {
        page.word_page = {
          ...page.word_page,
          valid: 0,
          word_len: 0,
          unicode: [],
        };
      });
    }
    if (scopeMatches(scope, 0xa4, 0)) {
      data.page_data.forEach((page) => clearRgbData(page, "frames"));
    }
    if (scopeMatches(scope, 0xa5, 0)) {
      data.page_data.forEach((page) => clearRgbData(page, "keyframes"));
    }
    if (scopeMatches(scope, 0xa6, 0x01)) {
      data.exchange_key = [];
      data.exchange_num = 0;
    }
    if (scopeMatches(scope, 0xa6, 0x04)) {
      data.Fn_key = [];
      data.Fn_key_num = 0;
    }
    if (scopeMatches(scope, 0xa6, 0x05)) {
      data.MACRO_key = [];
      data.MACRO_key_num = 0;
      data.macro_key = [];
    }
    if (scopeMatches(scope, 0xa6, 0x06)) {
      data.swap_key = [];
      data.swap_key_num = 0;
    }
    if (keyLayerScope(scope)) {
      data.key_layer = {
        ...data.key_layer,
        valid: 0,
        layer_num: 0,
        layer_data: [],
      };
    }
    if (scopeMatches(scope, 0xa6, 0x0e)) {
      data.tab_key = [];
      data.tab_key_num = 0;
    }
    return;
  }

  data.tab_key = [];
  data.tab_key_num = 0;
  data.MACRO_key = [];
  data.MACRO_key_num = 0;
  data.macro_key = [];
  data.exchange_key = [];
  data.exchange_num = 0;
  data.Fn_key = [];
  data.Fn_key_num = 0;
  data.swap_key = [];
  data.swap_key_num = 0;
  data.key_layer = {
    ...data.key_layer,
    valid: 0,
    layer_num: 0,
    layer_data: [],
  };
  data.page_data.forEach((page) => {
    page.valid = 0;
    page.word_page = {
      ...page.word_page,
      valid: 0,
      word_len: 0,
      unicode: [],
    };
    clearRgbData(page, "frames");
    clearRgbData(page, "keyframes");
    clearRgbData(page, "spotlight_frames");
  });
}

export function normalizeR6ProfileData(value) {
  const data = clone(value);
  data.page_data = list(data.page_data);
  data.tab_key = list(data.tab_key);
  data.key_layer = {
    valid: 0,
    layer_num: 0,
    layer_data: [],
    ...data.key_layer,
  };
  // AM Master exports MACRO_key while the web editor uses macro_key.  Its
  // files commonly contain both, with macro_key as an empty placeholder.
  const macroSource = list(data.macro_key).length
    ? data.macro_key
    : list(data.MACRO_key);
  if (macroSource.length) {
    data.macro_key = macroSource.map((item, index) => ({
      MACRO_key_index: item.MACRO_key_index ?? index,
      original_key:
        item.input_key ||
        item.original_key ||
        `#009515${index.toString(16).toUpperCase().padStart(2, "0")}`,
      layer_key: list(item.out_key || item.layer_key),
      intvel_ms: list(item.intvel_ms),
    }));
  }
  return data;
}

export function parseR6UploadFrames(
  uploadFrames,
  baseData = {},
  { strict = false, scope } = {}
) {
  const data = normalizeR6ProfileData(baseData);
  const source = list(uploadFrames).filter(
    (frame) => frame && frame.length >= 64
  );
  const pageCountFrame = framesOf(source, 0xa2, 0x06)[0];
  const uncertainty = framesOf(source, 0xa2, 0x01)[0];
  const valid = framesOf(source, 0xa2, 0x05)[0];
  const pageCount =
    uncertainty?.[2] ??
    valid?.[2] ??
    (pageCountFrame?.[2] || data.page_num || 0);
  if (strict) validateIndexedPackets(source);
  if (
    isFullUploadScope(scope) ||
    source.some((frame) => frame[0] === 0xa2 && frame[1] <= 0x06)
  ) {
    preparePages(data, pageCount);
  }
  clearUploadedCollections(data, scope);
  if (scopeMatches(scope, 0xa2, 0x01)) applyUncertainty(data, source);
  if (scopeMatches(scope, 0xa2, 0x02)) applyPageControls(data, source);
  if (scopeMatches(scope, 0xa2, 0x05)) applyValidBits(data, source);
  if (scopeMatches(scope, 0xa3, 0x01)) parseWordPages(data, source);
  if (keyLayerScope(scope)) parseKeyLayer(data, source, strict);
  if (scopeMatches(scope, 0xa6, 0x01)) parseExchangeKeys(data, source);
  if (scopeMatches(scope, 0xa6, 0x04)) parseFnKeys(data, source);
  if (scopeMatches(scope, 0xa6, 0x06)) parseSwapKeys(data, source);
  if (scopeMatches(scope, 0xa6, 0x05)) parseMacros(data, source);
  if (scopeMatches(scope, 0xa6, 0x0e)) parsePressHold(data, source);
  if (scopeMatches(scope, 0xa4, 0)) applyRgbFrames(data, source);
  if (scopeMatches(scope, 0xa5, 0)) applyRgbFrames(data, source);
  if (scopeMatches(scope, 0x0b, 0)) applyCarLightFrames(data, source);
  return data;
}

export function parseR6MacroFrames(
  uploadFrames,
  baseData = {},
  { legacy = false } = {}
) {
  const data = normalizeR6ProfileData(baseData);
  if (legacy) parseLegacyMacros(data, list(uploadFrames));
  else parseMacros(data, list(uploadFrames));
  return {
    MACRO_key_num: data.MACRO_key_num || 0,
    MACRO_key: list(data.MACRO_key),
    macro_key: list(data.macro_key),
  };
}

export function parseR6PressHoldFrames(uploadFrames, baseData = {}) {
  const data = normalizeR6ProfileData(baseData);
  parsePressHold(data, list(uploadFrames));
  return {
    tab_key_num: data.tab_key_num || 0,
    tab_key: list(data.tab_key),
  };
}
